import { useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { RulePicker } from './RulePicker.js';

interface VersionRow {
  readonly versionId: string;
  readonly versionNumber: string;
  readonly versionStatus: string;
  readonly isCurrent: boolean;
  readonly actorName: string | null;
  readonly actorCode: string | null;
}

export function M24VersionHistoryPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [ruleId, setRuleId] = useState('');
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  function onLoad(e: FormEvent): void {
    e.preventDefault();
    setError(null);
    setLoading(true);
    apiRequest<{ data: VersionRow[] }>(`/v1/rule-versions/rule/${encodeURIComponent(ruleId)}`, { token })
      .then((r) => { setVersions(r.data); setLoaded(true); })
      .catch((err) => { setError((err as Error).message); setLoaded(false); })
      .finally(() => setLoading(false));
  }

  return (
    <main aria-label="version-history">
      <h1>{t('m24.versionHistory.title')}</h1>
      <form aria-label="version-lookup-form" onSubmit={onLoad}>
        <RulePicker label={t('m24.versionHistory.ruleId')} value={ruleId} onPick={(id) => setRuleId(id)} />
        <button type="submit" disabled={loading || !ruleId.trim()}>{t('m24.versionHistory.submit')}</button>
      </form>
      {error && <p role="alert">{error}</p>}
      {loading && <p>{t('m24.versionHistory.loading')}</p>}
      {loaded && !loading && (
        <table aria-label="version-table">
          <thead>
            <tr><th>{t('m24.versionHistory.colVersion')}</th><th>{t('m24.versionHistory.colStatus')}</th><th>{t('m24.versionHistory.colCurrent')}</th><th>{t('m24.versionHistory.colCreatedBy')}</th></tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.versionId}>
                <td>{v.versionNumber}</td>
                <td>{v.versionStatus}</td>
                <td>{v.isCurrent ? t('m24.versionHistory.yes') : t('m24.versionHistory.no')}</td>
                <td>{[v.actorCode, v.actorName].filter(Boolean).join(' \u2014 ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
